// store/notificationsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/modules/shared/api/axios";

/* ---------- helpers ---------- */
const parseSeen = (seenStr) =>
  !seenStr || seenStr === "" ? [] : seenStr.split(",").filter(Boolean);

const addUuidToSeen = (seenStr, uuid) => {
  const arr = parseSeen(seenStr);
  if (!arr.includes(uuid)) arr.push(uuid);
  return arr.join(",");
};


/**
 * Fetch notifications relevant to userUuid.
 * (Server filters by Status/Recipient; client filters per-user read state.)
 */
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/notifications");
      const items = Array.isArray(data?.data) ? data.data : [];
      return { items };
    } catch (err) {
      return rejectWithValue(err?.message || err);
    }
  }
);

/**
 * Mark a single notification as read for userUuid.
 * - optimistic update: update local state first
 * - on failure: refetch from server
 */
export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async ({ id, userUuid }, { dispatch, getState, rejectWithValue }) => {
    try {
      const stateItems = getState().notifications.items;
      const notif = stateItems.find(
        (n) => n.ID === id || n.local_id === id
      );
      if (!notif) return rejectWithValue("Notification not found");

      const newSeen = addUuidToSeen(notif.Seen, userUuid);

      // optimistic local update
      dispatch(markReadLocal({ id, newSeen }));

      // remote update
      const recordId = notif.local_id || notif.ID;
      const response = await api.post(`/notifications/${recordId}/read`);

      return { id: notif.ID, updated: response?.data?.data };
    } catch (err) {
      // rollback: safe approach -> re-fetch full list so UI matches server
      dispatch(fetchNotifications());
      return rejectWithValue(err?.message || err);
    }
  }
);

/**
 * Mark all unread notifications as read for userUuid.
 */
export const markAllRead = createAsyncThunk(
  "notifications/markAllRead",
  async ({ userUuid }, { dispatch, getState, rejectWithValue }) => {
    try {
      const stateItems = getState().notifications.items;
      const unread = stateItems.filter(
        (n) => !parseSeen(n.Seen).includes(userUuid)
      );

      // optimistic local update
      dispatch(markAllReadLocal({ userUuid }));

      // remote updates (parallel)
      await api.post("/notifications/mark-all-read");
      return { count: unread.length };
    } catch (err) {
      // rollback by refreshing from server
      dispatch(fetchNotifications());
      return rejectWithValue(err?.message || err);
    }
  }
);

/* ---------- slice ---------- */
const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    items: [],
    loading: false,
    updating: false,
    error: null,
  },
  reducers: {
    // local optimistic update for a single notification
    markReadLocal(state, action) {
      const { id, newSeen } = action.payload;
      state.items = state.items.map((n) =>
        n.ID === id ? { ...n, Seen: newSeen } : n
      );
    },
    // local optimistic update for all unread notifications for userUuid
    markAllReadLocal(state, action) {
      const { userUuid } = action.payload;
      state.items = state.items.map((n) => ({
        ...n,
        Seen: addUuidToSeen(n.Seen, userUuid),
      }));
    },
    clearNotifications(state) {
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* fetch */
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      /* markAsRead */
      .addCase(markAsRead.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        state.updating = false;
        const updated = action.payload?.updated;
        if (!updated) {
          return;
        }
        state.items = state.items.map((item) =>
          item.ID === updated.ID || item.local_id === updated.local_id ? updated : item
        );
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error.message;
      })

      /* markAllRead */
      .addCase(markAllRead.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(markAllRead.fulfilled, (state, action) => {
        state.updating = false;
        state.items = state.items.map((item) => ({
          ...item,
          Seen: addUuidToSeen(item.Seen, action?.meta?.arg?.userUuid),
        }));
      })
      .addCase(markAllRead.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { markReadLocal, markAllReadLocal, clearNotifications } =
  notificationsSlice.actions;

/* ---------- selectors ---------- */

/**
 * All fetched notifications (Status + Recipient filtered server-side)
 */
export const selectNotifications = (state) => state.notifications.items;

/**
 * Unread notifications for a given userUuid
 */
export const selectUnreadByUser = (state, userUuid) =>
  state?.notifications?.items.filter((n) => !parseSeen(n.Seen).includes(userUuid));

/**
 * Number of unread notifications for a user
 */
export const selectUnreadCount = (state, userUuid) =>
  selectUnreadByUser(state, userUuid).length;

export default notificationsSlice.reducer;
